import { keepPreviousData } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useDebounce } from 'use-debounce';

import { useMy } from '@/hooks/useMy';
import { NodeDetails, Technology } from '@/types';
import { useMergeStatus } from '@/utils/api';

export interface Filter {
  search?: string;
  properties?: Record<string, string[]>;
  sort?: {
    orderBy: keyof Technology;
    descending: boolean;
  };
}

export function useGetTechnologies({
  enabled = true,
  filter,
}: {
  enabled?: boolean;
  filter?: Filter;
} = {}) {
  // Use 0ms delay when clearing search, 300ms when typing
  // This prevents the "empty table" bug when clearing a search that returned no results
  const searchValue = filter?.search || '';
  const [debouncedSearch] = useDebounce(
    searchValue,
    searchValue === '' ? 0 : 300
  );

  const { nodeFilters, isFiltersApplied } = useMemo(() => {
    const nodeFilters: NodeDetails['filters'] = [];

    if (debouncedSearch) {
      nodeFilters.push({
        field: '',
        operator: 'OR',
        value: [
          {
            field: 'name',
            operator: 'CONTAINS',
            value: debouncedSearch,
          },
          {
            field: 'vendor',
            operator: 'CONTAINS',
            value: debouncedSearch,
          },
          {
            field: 'product',
            operator: 'CONTAINS',
            value: debouncedSearch,
          },
          {
            field: 'version',
            operator: 'CONTAINS',
            value: debouncedSearch,
          },
        ],
      });
    }

    if (filter?.properties) {
      Object.entries(filter.properties).forEach(([key, value]) => {
        if (!value || value.length === 0) return;

        nodeFilters.push({
          field: key,
          operator: '=',
          value: value,
        });
      });
    }

    return {
      nodeFilters,
      isFiltersApplied: nodeFilters.length > 0,
    };
  }, [debouncedSearch, JSON.stringify(filter?.properties)]);

  const allQuery = useMy(
    {
      resource: 'technology',
      query: {
        node: {
          labels: ['Technology'],
        },
      },
    },
    { enabled }
  );

  const query = useMy(
    {
      resource: 'technology',
      query: {
        node: {
          labels: ['Technology'],
          filters: nodeFilters,
        },
        ...(filter?.sort
          ? {
              orderBy: filter.sort.orderBy,
              descending: filter.sort.descending,
            }
          : {}),
      },
    },
    {
      enabled,
      // Keep previous data visible during search to prevent blanking on every keystroke
      // Search is excluded from filterKey, so table doesn't remount
      // Filter/sort changes still remount via filterKey for scroll reset
      placeholderData: keepPreviousData,
    }
  );

  // BUGFIX: Deduplicate technologies by key to prevent duplicate rows in table
  // Backend may return duplicate Technology nodes in query results.
  // Same issue as assets (commit 6fb4c3171) - deduplicate to prevent duplicate table rows
  // Related: CHARIOT-1567
  const seenKeys = new Set<string>();
  const data = query.data.filter(technology => {
    if (seenKeys.has(technology.key)) {
      return false; // Skip duplicate
    }
    seenKeys.add(technology.key);
    return true; // Keep first occurrence
  });

  const status = useMergeStatus(allQuery.status, query.status);

  return {
    data,
    error: allQuery.error || query.error,
    status,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    refetch: () => {
      allQuery.refetch();
      query.refetch();
    },
    invalidate: () => {
      allQuery.invalidate();
      query.invalidate();
    },
    isAllEmpty: status !== 'pending' && allQuery.data.length === 0,
    isFilteredEmpty: status !== 'pending' && data.length === 0,
    isFiltersApplied,
  };
}
